import { groupBy, map } from 'lodash';
import { DateTime } from 'luxon';

import { BoardMembers } from '../components/business/editTabs/kyc/CompanyRepresentation';

export type RelatedIndividual = {
  relation?: string;
  entityId: string;
  entityType: string;
  name?: string;
}

type BoardMember = BoardMembers['boardMembers'][number];
type BoardMemberRole = BoardMember['roles'][number];

export const BOARD_MEMBER_RELATION = 'Board member';

export const isCurrentBoardMemberRole = (
  { toDate }: BoardMemberRole,
  now: DateTime = DateTime.now(),
): boolean => {
  if (!toDate) return true;

  const endOfRole = DateTime.fromISO(toDate);
  if (!endOfRole.isValid) return true;

  return endOfRole.endOf('day') >= now;
};

export const boardMembersToRelatedIndividuals = (
  boardMembers: BoardMember[] | undefined,
): RelatedIndividual[] => (boardMembers ?? []).flatMap(({
  entityId,
  entityType,
  fullName,
}) => ((entityId && entityType) ? [{
  entityId,
  entityType,
  name: fullName,
  relation: BOARD_MEMBER_RELATION,
}] : []));

export const currentBoardMembersToRelatedIndividuals = (
  boardMembers: BoardMember[] | undefined,
  now: DateTime = DateTime.now(),
): RelatedIndividual[] => (boardMembers ?? []).flatMap((member) => {
  const {
    entityId,
    entityType,
    fullName,
    roles,
  } = member;

  if (!entityId || !entityType) return [];

  const currentRoles = (roles ?? []).filter((role) => isCurrentBoardMemberRole(role, now));
  if (roles?.length && !currentRoles.length) return [];

  const relations = currentRoles.length
    ? currentRoles.map(({ roleName }) => roleName?.trim() || BOARD_MEMBER_RELATION)
    : [BOARD_MEMBER_RELATION];

  return relations.map((relation) => ({
    entityId,
    entityType,
    name: fullName,
    relation,
  }));
});

export const mergeByEntityId = (
  relatedIndividuals: RelatedIndividual[],
): RelatedIndividual[] => map(
  groupBy(relatedIndividuals, 'entityId'),
  (grpEntities): RelatedIndividual => {
    const baseEntity = grpEntities[0];

    const relations = grpEntities
      .map((e) => e.relation)
      .filter((value, index, self) => self.indexOf(value) === index)
      .join(', ');

    return {
      ...baseEntity,
      relation: relations,
    };
  },
);
